import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  IdCard,
  ShieldCheck,
  ClipboardCheck,
  UploadCloud,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Lock,
  ChevronDown,
  RefreshCw,
} from "lucide-react";

const FONT_DISPLAY = "'Fraunces', ui-serif, Georgia, serif";
const FONT_BODY = "'Inter', ui-sans-serif, system-ui, sans-serif";
const FONT_MONO = "'IBM Plex Mono', ui-monospace, SFMono-Regular, monospace";

// ---------------------------------------------------------------------
// API layer — talks to the real Django endpoints, using the same base-URL
// pattern as the rest of the app (e.g. the login call):
//   `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/api/...`
// Adjust API_PREFIX below to match wherever verification_app.urls is
// mounted in your root urls.py (e.g. path("api/verification/", include(...))).
// ---------------------------------------------------------------------
const API_ROOT = process.env.REACT_APP_API_URL || "http://localhost:8000";
const API_PREFIX = "/api"; // confirmed: license/insurance/pevo/badge are mounted flat under /api/

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10MB — keep in sync with server-side limit
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const POLL_INTERVAL_MS = 2500;
const POLL_MAX_ATTEMPTS = 24; // ~1 minute of polling before we stop and say "still reviewing"

function validateFile(file) {
  if (!ACCEPTED_TYPES.includes(file.type)) return "Please upload a JPG, PNG, or WEBP image.";
  if (file.size > MAX_FILE_BYTES) return "That file is over 10MB — try a smaller photo.";
  return null;
}

function buildApi(getAuthHeaders) {
  async function request(path, { method = "GET", body, isFormData = false } = {}) {
    const headers = { ...(await getAuthHeaders()) };
    if (!isFormData) headers["Content-Type"] = "application/json";

    const response = await fetch(`${API_ROOT}${API_PREFIX}${path}`, {
      method,
      headers,
      credentials: "include", // keep session-cookie auth working alongside token auth
      body: isFormData ? body : body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      let detail = `Request failed (${response.status})`;
      try {
        const data = await response.json();
        detail = data.detail || JSON.stringify(data);
      } catch {
        /* non-JSON error body, keep generic message */
      }
      throw new Error(detail);
    }
    return response.status === 204 ? null : response.json();
  }

  return {
    listLicense: () => request("/license/"),
    createLicense: (formData) => request("/license/", { method: "POST", body: formData, isFormData: true }),
    getLicense: (id) => request(`/license/${id}/`),

    listInsurance: () => request("/insurance/"),
    createInsurance: (formData) => request("/insurance/", { method: "POST", body: formData, isFormData: true }),
    getInsurance: (id) => request(`/insurance/${id}/`),

    listPevo: () => request("/pevo/"),
    createPevo: (formData) => request("/pevo/", { method: "POST", body: formData, isFormData: true }),
    getPevo: (id) => request(`/pevo/${id}/`),

    getBadge: (requiresPevo) => request(`/badge/?requires_pevo=${requiresPevo ? "true" : "false"}`),
  };
}

// Maps raw backend `status` values -> the UI's honest display buckets.
// Mirrors the vocabulary in DriverVerificationBadge.jsx so the two
// components never disagree about what a status means.
function displayStatus(kind, record) {
  if (!record) return "empty";
  const s = record.status;
  if (s === "pending" || s === "flagged") {
    // Insurance's heuristic-only path always lands in "flagged" by design
    // (see scoring.decide_insurance_status) — that's "on file", not an error.
    if (kind === "insurance" && s === "flagged" && (record.failure_reasons || []).includes("heuristic_only_requires_review")) {
      return "on_file";
    }
    return "reviewing";
  }
  if (s === "auto_verified" || s === "manual_verified" || s === "carrier_verified" || s === "verified") {
    return "confirmed";
  }
  if (s === "rejected" || s === "expired") return "needs_attention";
  return "reviewing";
}

function reasonMessage(record) {
  const reasons = record?.failure_reasons || [];
  if (reasons.includes("license_expired") || reasons.includes("policy_expired")) {
    return "This document has expired. Upload a current one to continue.";
  }
  if (reasons.includes("named_insured_mismatch")) {
    return "The name on this insurance card doesn't match your verified license. Double-check you uploaded your own card.";
  }
  if (reasons.some((r) => String(r).startsWith("mismatched_fields"))) {
    return "Some details on the front and back of the license don't match. Try a clearer, unedited photo of both sides.";
  }
  if (reasons.includes("barcode_decode_failed")) {
    return "We couldn't read the barcode on the back of the license. Retake the photo with better lighting and less glare.";
  }
  if (reasons.includes("low_confidence_score")) {
    return "This document didn't pass our automatic checks. A reviewer will follow up, or you can try re-uploading a clearer photo.";
  }
  return "This submission needs another look. Upload a current, clear photo to try again.";
}

// ---------------------------------------------------------------------
// UI atoms
// ---------------------------------------------------------------------
const STATUS_STYLES = {
  confirmed: { label: "Confirmed", color: "#3F6B4F", bg: "#3F6B4F14" },
  on_file: { label: "On file — pending carrier confirmation", color: "#A9812F", bg: "#A9812F14" },
  reviewing: { label: "Review in progress", color: "#7C8489", bg: "#7C848914" },
  needs_attention: { label: "Needs attention", color: "#B5432E", bg: "#B5432E14" },
  locked: { label: "Complete the previous step first", color: "#B0AA9B", bg: "#B0AA9B10" },
  empty: { label: "Not started", color: "#9A9384", bg: "#9A938410" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-medium"
      style={{ color: s.color, backgroundColor: s.bg, fontFamily: FONT_BODY }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} />
      {s.label}
    </span>
  );
}

function MiniDropzone({ label, file, onFile, onClear, error, disabled }) {
  const inputId = `f-${label.replace(/\s+/g, "-").toLowerCase()}`;
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = (f) => {
    if (!f) return;
    const problem = validateFile(f);
    onFile(f, problem);
  };

  return (
    <div>
      <label
        htmlFor={inputId}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (!disabled) handleFiles(e.dataTransfer.files?.[0]);
        }}
        className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed px-3 py-5 text-center cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-[#12203A] focus-within:ring-offset-2 ${
          disabled
            ? "opacity-50 cursor-not-allowed border-[#E4DFD1] bg-[#FBF9F4]"
            : file
            ? "border-[#3F6B4F] bg-[#3F6B4F0A]"
            : dragOver
            ? "border-[#A9812F] bg-[#A9812F0D]"
            : "border-[#D9D3C4] bg-[#FBF9F4] hover:border-[#B0AA9B]"
        }`}
      >
        {file ? (
          <CheckCircle2 size={18} className="text-[#3F6B4F]" />
        ) : (
          <UploadCloud size={18} strokeWidth={1.5} className="text-[#6B6558]" />
        )}
        <span className="text-[12px] font-medium text-[#3A3733]" style={{ fontFamily: FONT_BODY }}>
          {label}
        </span>
        <span className="text-[11px] text-[#9A9384] truncate max-w-full" style={{ fontFamily: FONT_BODY }}>
          {file ? file.name : "Click or drag a photo"}
        </span>
        <input
          id={inputId}
          type="file"
          accept={ACCEPTED_TYPES.join(",")}
          disabled={disabled}
          className="sr-only"
          onChange={(e) => handleFiles(e.target.files?.[0])}
        />
      </label>
      {file && !disabled && (
        <button
          onClick={onClear}
          className="text-[11px] text-[#9A9384] hover:text-[#B5432E] mt-1"
          style={{ fontFamily: FONT_BODY }}
        >
          Remove
        </button>
      )}
      {error && (
        <p className="text-[11px] text-[#B5432E] mt-1" style={{ fontFamily: FONT_BODY }}>
          {error}
        </p>
      )}
    </div>
  );
}

function ExtractedFields({ record, fieldMap }) {
  if (!record) return null;
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-3 pt-3 border-t border-[#E4DFD1]">
      {fieldMap.map(([label, key]) => (
        <div key={key}>
          <div className="text-[10px] uppercase tracking-wide text-[#9A9384]" style={{ fontFamily: FONT_BODY }}>
            {label}
          </div>
          <div className="text-[13px] text-[#211F1C]" style={{ fontFamily: FONT_MONO }}>
            {record[key] ?? "—"}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------
// Step: License (front + back + selfie)
// ---------------------------------------------------------------------
function LicenseStep({ api, record, onResolved, expanded, onToggle }) {
  const [files, setFiles] = useState({ front: null, back: null, selfie: null });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const status = displayStatus("license", record);
  const canSubmit = files.front && files.back && files.selfie && !submitting;

  const handleFile = (key) => (file, problem) => {
    setErrors((e) => ({ ...e, [key]: problem }));
    setFiles((f) => ({ ...f, [key]: problem ? null : file }));
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const formData = new FormData();
      formData.append("front_image", files.front);
      formData.append("back_image", files.back);
      formData.append("selfie_image", files.selfie);
      const created = await api.createLicense(formData);
      onResolved(created); // parent starts polling from here
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StepShell
      icon={IdCard}
      title="Driver's License"
      subtitle="Front and back photos, plus a quick selfie."
      status={status}
      expanded={expanded}
      onToggle={onToggle}
      confirmed={status === "confirmed"}
    >
      {status === "empty" && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <MiniDropzone label="Front" file={files.front} error={errors.front} onFile={handleFile("front")} onClear={() => handleFile("front")(null, null)} />
            <MiniDropzone label="Back" file={files.back} error={errors.back} onFile={handleFile("back")} onClear={() => handleFile("back")(null, null)} />
            <MiniDropzone label="Selfie" file={files.selfie} error={errors.selfie} onFile={handleFile("selfie")} onClear={() => handleFile("selfie")(null, null)} />
          </div>
          <CheckList
            items={[
              "The barcode on the back is decoded and cross-checked against the printed details",
              "Expiration date and format are validated",
              "The selfie confirms the license photo matches you",
            ]}
          />
          <SubmitRow error={submitError} disabled={!canSubmit} submitting={submitting} onSubmit={submit} label="Submit for verification" />
        </>
      )}

      {status === "reviewing" && <ReviewingNote fileName="License documents" />}

      {status === "confirmed" && (
        <ExtractedFields
          record={record}
          fieldMap={[
            ["Name", "ocr_name"],
            ["License number", "ocr_license_number"],
            ["State", "ocr_state"],
            ["Expires", "ocr_expiration"],
          ]}
        />
      )}

      {status === "needs_attention" && (
        <>
          <p className="text-[13px] text-[#B5432E] mb-3" style={{ fontFamily: FONT_BODY }}>
            {reasonMessage(record)}
          </p>
          <div className="grid grid-cols-3 gap-3">
            <MiniDropzone label="Front" file={files.front} error={errors.front} onFile={handleFile("front")} onClear={() => handleFile("front")(null, null)} />
            <MiniDropzone label="Back" file={files.back} error={errors.back} onFile={handleFile("back")} onClear={() => handleFile("back")(null, null)} />
            <MiniDropzone label="Selfie" file={files.selfie} error={errors.selfie} onFile={handleFile("selfie")} onClear={() => handleFile("selfie")(null, null)} />
          </div>
          <SubmitRow error={submitError} disabled={!canSubmit} submitting={submitting} onSubmit={submit} label="Resubmit" />
        </>
      )}
    </StepShell>
  );
}

// ---------------------------------------------------------------------
// Step: Insurance (single card image, linked to the license record)
// ---------------------------------------------------------------------
function InsuranceStep({ api, record, licenseVerificationId, locked, onResolved, expanded, onToggle }) {
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const status = locked ? "locked" : displayStatus("insurance", record);

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const formData = new FormData();
      formData.append("license_verification", licenseVerificationId);
      formData.append("card_image", file);
      const created = await api.createInsurance(formData);
      onResolved(created);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StepShell
      icon={ShieldCheck}
      title="Insurance"
      subtitle={locked ? "Unlocks once your license is confirmed." : "A photo of your current insurance ID card."}
      status={status}
      expanded={expanded}
      onToggle={onToggle}
      confirmed={status === "confirmed"}
      locked={locked}
    >
      {status === "empty" && (
        <>
          <MiniDropzone
            label="Insurance card"
            file={file}
            error={error}
            onFile={(f, problem) => {
              setError(problem);
              setFile(problem ? null : f);
            }}
            onClear={() => setFile(null)}
          />
          <CheckList
            items={[
              "Named insured is cross-checked against your verified license",
              "Card expiration is checked",
              "Shows “Confirmed” only once verified directly with your carrier — otherwise it stays “on file”",
            ]}
          />
          <SubmitRow error={submitError} disabled={!file || submitting} submitting={submitting} onSubmit={submit} label="Submit for verification" />
        </>
      )}

      {status === "reviewing" && <ReviewingNote fileName="Insurance card" />}

      {(status === "on_file" || status === "confirmed") && (
        <>
          <ExtractedFields
            record={record}
            fieldMap={[
              ["Named insured", "ocr_named_insured"],
              ["Carrier", "ocr_carrier_name"],
              ["Policy #", "ocr_policy_number"],
              ["Expires", "ocr_expiration_date"],
            ]}
          />
          {status === "on_file" && (
            <p className="text-[12px] text-[#A9812F] mt-3" style={{ fontFamily: FONT_BODY }}>
              We've matched this to your license details. We haven't yet confirmed it's active with your carrier.
            </p>
          )}
        </>
      )}

      {status === "needs_attention" && (
        <>
          <p className="text-[13px] text-[#B5432E] mb-3" style={{ fontFamily: FONT_BODY }}>
            {reasonMessage(record)}
          </p>
          <MiniDropzone
            label="Insurance card"
            file={file}
            error={error}
            onFile={(f, problem) => {
              setError(problem);
              setFile(problem ? null : f);
            }}
            onClear={() => setFile(null)}
          />
          <SubmitRow error={submitError} disabled={!file || submitting} submitting={submitting} onSubmit={submit} label="Resubmit" />
        </>
      )}
    </StepShell>
  );
}

// ---------------------------------------------------------------------
// Step: PEVO (form fields + optional cert image)
// ---------------------------------------------------------------------
const US_STATES = ["WA", "TX", "UT", "CO", "GA", "KS", "MN", "AZ", "FL", "NC", "OK", "PA", "VA"];

function PevoStep({ api, record, locked, onResolved, expanded, onToggle }) {
  const [form, setForm] = useState({ issuing_state: "", issuing_provider: "", cert_number: "" });
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const status = locked ? "locked" : displayStatus("pevo", record);
  const canSubmit = form.issuing_state && form.issuing_provider && form.cert_number && !submitting;

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => formData.append(k, v));
      if (file) formData.append("cert_image", file);
      const created = await api.createPevo(formData);
      onResolved(created);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StepShell
      icon={ClipboardCheck}
      title="Pilot Car Certification"
      subtitle={locked ? "Unlocks once your insurance is submitted." : "Your state escort/pilot-car certification."}
      status={status}
      expanded={expanded}
      onToggle={onToggle}
      confirmed={status === "confirmed"}
      locked={locked}
      isLast
    >
      {status === "empty" && (
        <>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <FormField label="Issuing state">
              <select
                value={form.issuing_state}
                onChange={(e) => setForm((f) => ({ ...f, issuing_state: e.target.value }))}
                className="w-full text-[13px] rounded-lg border border-[#D9D3C4] px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#12203A]"
                style={{ fontFamily: FONT_BODY }}
              >
                <option value="">Select…</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Certification #">
              <input
                type="text"
                value={form.cert_number}
                onChange={(e) => setForm((f) => ({ ...f, cert_number: e.target.value }))}
                className="w-full text-[13px] rounded-lg border border-[#D9D3C4] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#12203A]"
                style={{ fontFamily: FONT_MONO }}
              />
            </FormField>
          </div>
          <FormField label="Issuing training provider" className="mb-3">
            <input
              type="text"
              placeholder="e.g. Evergreen Safety Council"
              value={form.issuing_provider}
              onChange={(e) => setForm((f) => ({ ...f, issuing_provider: e.target.value }))}
              className="w-full text-[13px] rounded-lg border border-[#D9D3C4] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#12203A]"
              style={{ fontFamily: FONT_BODY }}
            />
          </FormField>
          <MiniDropzone
            label="Certification card (optional)"
            file={file}
            error={error}
            onFile={(f, problem) => {
              setError(problem);
              setFile(problem ? null : f);
            }}
            onClear={() => setFile(null)}
          />
          <p className="text-[11px] text-[#9A9384] mt-2" style={{ fontFamily: FONT_BODY }}>
            Some states confirm certifications online; others require a manual check, which can take a bit longer.
          </p>
          <SubmitRow error={submitError} disabled={!canSubmit} submitting={submitting} onSubmit={submit} label="Submit for verification" />
        </>
      )}

      {status === "reviewing" && (
        <ReviewingNote
          fileName={record?.issuing_state ? `${record.issuing_state} certification` : "Certification"}
          note={
            record?.verification_method === "manual_phone" || record?.verification_method === "manual_admin"
              ? "This state requires manual confirmation, so it may take a bit longer than the automatic checks."
              : undefined
          }
        />
      )}

      {status === "confirmed" && (
        <ExtractedFields
          record={record}
          fieldMap={[
            ["Issuing state", "issuing_state"],
            ["Expires", "expiration_date"],
          ]}
        />
      )}

      {status === "needs_attention" && (
        <p className="text-[13px] text-[#B5432E]" style={{ fontFamily: FONT_BODY }}>
          {reasonMessage(record)}
        </p>
      )}
    </StepShell>
  );
}

// ---------------------------------------------------------------------
// Shared shell / small building blocks
// ---------------------------------------------------------------------
function FormField({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[11px] uppercase tracking-wide text-[#9A9384] mb-1 block" style={{ fontFamily: FONT_BODY }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function CheckList({ items }) {
  return (
    <details className="mt-3 group">
      <summary className="text-[12px] text-[#6B6558] cursor-pointer list-none" style={{ fontFamily: FONT_BODY }}>
        <span className="underline decoration-[#D9D3C4] underline-offset-2">What we check</span>
      </summary>
      <ul className="mt-2 space-y-1">
        {items.map((c) => (
          <li key={c} className="text-[12px] text-[#8A8474] pl-3 relative before:content-['–'] before:absolute before:left-0" style={{ fontFamily: FONT_BODY }}>
            {c}
          </li>
        ))}
      </ul>
    </details>
  );
}

function SubmitRow({ error, disabled, submitting, onSubmit, label }) {
  return (
    <div className="mt-4">
      {error && (
        <p className="text-[12px] text-[#B5432E] mb-2" style={{ fontFamily: FONT_BODY }}>
          {error}
        </p>
      )}
      <button
        onClick={onSubmit}
        disabled={disabled}
        className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-[13px] font-medium text-white transition-opacity disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        style={{ backgroundColor: "#12203A", fontFamily: FONT_BODY }}
      >
        {submitting && <RefreshCw size={13} className="animate-spin" />}
        {submitting ? "Uploading…" : label}
      </button>
    </div>
  );
}

function ReviewingNote({ fileName, note }) {
  return (
    <div className="flex items-start gap-2 text-[13px] text-[#6B6558]" style={{ fontFamily: FONT_BODY }}>
      <Clock size={15} className="mt-0.5 shrink-0" />
      <div>
        <p>
          <span className="font-medium text-[#3A3733]">{fileName}</span> is being reviewed. This updates
          automatically — no need to refresh.
        </p>
        {note && <p className="text-[#A9812F] mt-1">{note}</p>}
      </div>
    </div>
  );
}

function StepShell({ icon: Icon, title, subtitle, status, expanded, onToggle, children, confirmed, locked, isLast }) {
  return (
    <div className="relative pl-14">
      {!isLast && (
        <div
          className="absolute left-[22px] top-11 bottom-[-28px] w-px opacity-70"
          style={{ backgroundImage: "repeating-linear-gradient(180deg, #C9BE9E 0 6px, transparent 6px 12px)" }}
        />
      )}
      <div
        className="absolute left-0 top-0 w-11 h-11 rounded-full flex items-center justify-center ring-4 ring-[#F7F5F0]"
        style={{ backgroundColor: confirmed ? "#12203A" : "#EFEBE1" }}
      >
        {confirmed ? (
          <CheckCircle2 size={19} className="text-[#D9C98A]" strokeWidth={2} />
        ) : locked ? (
          <Lock size={16} className="text-[#B0AA9B]" strokeWidth={2} />
        ) : (
          <Icon size={18} className="text-[#3A3733]" strokeWidth={1.75} />
        )}
      </div>

      <div className="bg-white rounded-2xl border border-[#E4DFD1] mb-7 overflow-hidden">
        <button
          onClick={() => !locked && onToggle()}
          disabled={locked}
          className={`w-full flex items-center justify-between gap-4 px-6 py-4 text-left ${locked ? "cursor-not-allowed" : ""}`}
        >
          <div>
            <div className="flex items-center gap-2.5">
              <h3 className="text-[17px] font-semibold text-[#12203A] tracking-tight" style={{ fontFamily: FONT_DISPLAY }}>
                {title}
              </h3>
              {status === "needs_attention" && <AlertTriangle size={14} className="text-[#B5432E]" />}
            </div>
            <p className="text-[13px] text-[#6B6558] mt-0.5" style={{ fontFamily: FONT_BODY }}>
              {subtitle}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <StatusPill status={status} />
            {!locked && <ChevronDown size={16} className={`text-[#9A9384] transition-transform ${expanded ? "rotate-180" : ""}`} />}
          </div>
        </button>
        {expanded && !locked && <div className="px-6 pb-6">{children}</div>}
      </div>
    </div>
  );
}

function ProgressRing({ completed, total }) {
  const pct = total ? completed / total : 0;
  const r = 26;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative w-16 h-16 shrink-0">
      <svg viewBox="0 0 64 64" className="w-16 h-16 -rotate-90">
        <circle cx="32" cy="32" r={r} fill="none" stroke="#2A3B5C" strokeWidth="5" />
        <circle
          cx="32"
          cy="32"
          r={r}
          fill="none"
          stroke="#D9C98A"
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          style={{ transition: "stroke-dashoffset 0.5s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[14px] font-semibold text-white" style={{ fontFamily: FONT_MONO }}>
          {completed}/{total}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------
export default function VerificationFlow({
  getAuthHeaders = async () => ({}), // override to return e.g. { Authorization: `Bearer ${token}` }
  requiresPevo = true,
}) {
  const api = useRef(buildApi(getAuthHeaders)).current;

  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [license, setLicense] = useState(null);
  const [insurance, setInsurance] = useState(null);
  const [pevo, setPevo] = useState(null);
  const [expandedStep, setExpandedStep] = useState("license");

  const pollTimers = useRef({});

  // ---- initial load: resume in-progress verifications on mount --------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [licenseList, insuranceList, pevoList] = await Promise.all([
          api.listLicense(),
          api.listInsurance(),
          requiresPevo ? api.listPevo() : Promise.resolve([]),
        ]);
        if (cancelled) return;
        const latestLicense = licenseList?.[0] || null;
        const latestInsurance = insuranceList?.[0] || null;
        const latestPevo = pevoList?.[0] || null;
        setLicense(latestLicense);
        setInsurance(latestInsurance);
        setPevo(latestPevo);
        if (latestLicense && ["pending", "flagged"].includes(latestLicense.status)) startPolling("license", latestLicense.id);
        if (latestInsurance && ["pending", "flagged"].includes(latestInsurance.status)) startPolling("insurance", latestInsurance.id);
        if (latestPevo && ["pending", "flagged"].includes(latestPevo.status)) startPolling("pevo", latestPevo.id);
      } catch (err) {
        if (!cancelled) setLoadError(err.message);
      } finally {
        if (!cancelled) setLoadingInitial(false);
      }
    })();
    return () => {
      cancelled = true;
      Object.values(pollTimers.current).forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setters = { license: setLicense, insurance: setInsurance, pevo: setPevo };
  const getters = { license: api.getLicense, insurance: api.getInsurance, pevo: api.getPevo };

  const startPolling = useCallback((kind, id, attempt = 0) => {
    clearTimeout(pollTimers.current[kind]);
    pollTimers.current[kind] = setTimeout(async () => {
      try {
        const updated = await getters[kind](id);
        setters[kind](updated);
        const terminal =
          !["pending", "flagged"].includes(updated.status) ||
          (kind === "insurance" && (updated.failure_reasons || []).includes("heuristic_only_requires_review"));
        if (!terminal && attempt < POLL_MAX_ATTEMPTS) {
          startPolling(kind, id, attempt + 1);
        }
      } catch {
        // transient network error while polling — try again on the next tick
        if (attempt < POLL_MAX_ATTEMPTS) startPolling(kind, id, attempt + 1);
      }
    }, POLL_INTERVAL_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResolved = (kind) => (created) => {
    setters[kind](created);
    if (["pending", "flagged"].includes(created.status)) startPolling(kind, created.id);
    if (kind === "license") setExpandedStep("insurance");
    if (kind === "insurance") setExpandedStep("pevo");
  };

  const licenseConfirmed = license && ["auto_verified", "manual_verified"].includes(license.status);
  const insuranceSubmitted = !!insurance;
  const completedCount = [
    licenseConfirmed,
    insurance && ["auto_verified", "carrier_verified", "manual_verified"].includes(insurance.status),
    requiresPevo ? pevo && pevo.status === "verified" : true,
  ].filter(Boolean).length;
  const totalSteps = requiresPevo ? 3 : 2;
  const allDone = completedCount === totalSteps;

  if (loadingInitial) {
    return (
      <div className="w-full max-w-2xl mx-auto py-16 flex items-center justify-center gap-2 text-[#6B6558]" style={{ fontFamily: FONT_BODY }}>
        <RefreshCw size={16} className="animate-spin" /> Loading your verification status…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="w-full max-w-2xl mx-auto py-10 text-center" style={{ fontFamily: FONT_BODY }}>
        <AlertTriangle size={20} className="text-[#B5432E] mx-auto mb-2" />
        <p className="text-[14px] text-[#B5432E] mb-3">Couldn't load your verification status: {loadError}</p>
        <button onClick={() => window.location.reload()} className="text-[13px] underline text-[#12203A]">
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto" style={{ fontFamily: FONT_BODY }}>
      <div className="rounded-2xl px-7 py-6 mb-8 flex items-center justify-between gap-6" style={{ backgroundColor: "#12203A" }}>
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8FA0BE] mb-1.5" style={{ fontFamily: FONT_MONO }}>
            Driver Onboarding
          </p>
          <h2 className="text-[22px] text-white font-semibold tracking-tight" style={{ fontFamily: FONT_DISPLAY }}>
            {allDone ? "You're fully verified" : "Complete your verification"}
          </h2>
          <p className="text-[13px] text-[#C7D0E0] mt-1 max-w-sm">
            {allDone
              ? "Every credential is confirmed and current. Dispatch companies can see your verified badge."
              : "A few minutes total. Each document is checked automatically, with a person reviewing anything unclear."}
          </p>
        </div>
        <ProgressRing completed={completedCount} total={totalSteps} />
      </div>

      <LicenseStep
        api={api}
        record={license}
        onResolved={handleResolved("license")}
        expanded={expandedStep === "license"}
        onToggle={() => setExpandedStep((s) => (s === "license" ? null : "license"))}
      />
      <InsuranceStep
        api={api}
        record={insurance}
        licenseVerificationId={license?.id}
        locked={!licenseConfirmed}
        onResolved={handleResolved("insurance")}
        expanded={expandedStep === "insurance"}
        onToggle={() => setExpandedStep((s) => (s === "insurance" ? null : "insurance"))}
      />
      {requiresPevo && (
        <PevoStep
          api={api}
          record={pevo}
          locked={!insuranceSubmitted}
          onResolved={handleResolved("pevo")}
          expanded={expandedStep === "pevo"}
          onToggle={() => setExpandedStep((s) => (s === "pevo" ? null : "pevo"))}
        />
      )}

      <p className="text-[12px] text-[#9A9384] text-center mt-2 mb-4">
        Your documents are reviewed for verification purposes only and stored securely.
      </p>
    </div>
  );
}
